import { BuyableItemType, CreditNoteStatus, FileType, Prisma } from "@prisma/client";

export type OrderItemWithTicketOrBadge = {
  ticket?: {
    code?: string | null;
    ticketType?: { name?: string | null } | null;
  } | null;
  badge?: {
    code?: string | null;
    badgeType?: { name?: string | null } | null;
  } | null;
};

export type CreditNoteFilters = {
  searchText?: string;
  status?: CreditNoteStatus;
};

export const orderForCreditNoteAccessValues =
  Prisma.validator<Prisma.OrderDefaultArgs>()({
    select: {
      id: true,
      reference: true,
      onlineDetails: {
        select: {
          user: {
            select: {
              id: true,
              connectId: true,
              creditNoteCounter: true,
            },
          },
        },
      },
      creditNotes: {
        where: {
          status: {
            in: [
              CreditNoteStatus.awaiting,
              CreditNoteStatus.awaitingApproval,
              CreditNoteStatus.accepted,
              CreditNoteStatus.refused,
            ],
          },
        },
        select: {
          id: true,
          status: true,
          creditNoteItems: {
            select: {
              orderItemId: true,
            },
          },
        },
      },
      orderItems: {
        select: {
          basePrice: true,
          finalTotal: true,
          id: true,
          ticket: {
            select: {
              code: true,
              ticketType: {
                select: {
                  eventId: true,
                  event: {
                    select: {
                      organiserId: true,
                    },
                  },
                  pricingId: true,
                },
              },
            },
          },
          badge: {
            select: {
              code: true,
              badgeType: {
                select: {
                  eventId: true,
                  event: {
                    select: {
                      organiserId: true,
                    },
                  },
                  pricingId: true,
                },
              },
            },
          },
        },
      },
    },
  });

export type OrderForCreditNoteAccess = Prisma.OrderGetPayload<
  typeof orderForCreditNoteAccessValues
>;

export const creditNoteWithOrderItemsValues =
  Prisma.validator<Prisma.CreditNoteDefaultArgs>()({
    include: {
      order: {
        include: {
          orderItems: {
            select: {
              basePrice: true,
              id: true,
              finalTotal: true,
              ticket: {
                select: {
                  code: true,
                  ticketType: {
                    select: {
                      eventId: true,
                      pricingId: true,
                      event: { select: { organiserId: true } },
                    },
                  },
                },
              },
              badge: {
                select: {
                  code: true,
                  badgeType: {
                    select: {
                      eventId: true,
                      pricingId: true,
                      event: { select: { organiserId: true } },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  });

export type CreditNoteWithOrderItems = Prisma.CreditNoteGetPayload<
  typeof creditNoteWithOrderItemsValues
>;

export const creditNoteForStatusUpdateValues =
  Prisma.validator<Prisma.CreditNoteDefaultArgs>()({
    select: {
      id: true,
      status: true,
      orderId: true,
      reference: true,
      refundedAt: true,
      refundStatus: true,
      refundError: true,
      refundTransactionNumber: true,
      order: {
        select: {
          reference: true,
          total: true,
          cashRegisterId: true,
          cashRegisterDetailsId: true,
          onlineCardPayment: {
            select: {
              transactionNumber: true,
              callNumber: true,
            },
          },
        },
      },
      creditNoteItems: {
        select: {
          orderItem: {
            select: {
              id: true,
              finalTotal: true,
              vatRate: true,
              ticket: {
                select: {
                  id: true,
                  code: true,
                  isRefunded: true,
                  ScannedTicket: {
                    select: { id: true },
                  },
                  ticketType: {
                    select: {
                      id: true,
                      isQuantityInfinite: true,
                      quantityLeft: true,
                    },
                  },
                },
              },
              badge: {
                select: {
                  id: true,
                  code: true,
                  isEnabled: true,
                  isRefunded: true,
                  ScannedTicket: {
                    select: { id: true },
                  },
                  badgeType: {
                    select: {
                      id: true,
                      isQuantityInfinite: true,
                      quantityLeft: true,
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  });

export type CreditNoteForStatusUpdate = Prisma.CreditNoteGetPayload<
  typeof creditNoteForStatusUpdateValues
>;

export const creditNoteForListValues =
  Prisma.validator<Prisma.CreditNoteDefaultArgs>()({
    include: {
      file: true,
      order: {
        select: {
          id: true,
          reference: true,
          total: true,
          onlineDetails: {
            select: {
              billingFirstName: true,
              billingLastName: true,
              user: {
                select: {
                  email: true,
                },
              },
            },
          },
        },
      },
      creditNoteItems: {
        include: {
          orderItem: {
            select: {
              id: true,
              ticket: {
                select: {
                  code: true,
                  ticketType: {
                    select: {
                      name: true,
                      event: {
                        select: {
                          name: true,
                          organiser: {
                            select: {
                              admin: {
                                select: {
                                  businessName: true,
                                  firstName: true,
                                  lastName: true,
                                  email: true,
                                },
                              },
                            },
                          },
                        },
                      },
                      activity: {
                        select: {
                          structure: {
                            select: {
                              businessName: true,
                              admin: {
                                select: {
                                  businessName: true,
                                  firstName: true,
                                  lastName: true,
                                  email: true,
                                },
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
              badge: {
                select: {
                  code: true,
                  badgeType: {
                    select: {
                      name: true,
                      event: {
                        select: {
                          name: true,
                          organiser: {
                            select: {
                              admin: {
                                select: {
                                  businessName: true,
                                  firstName: true,
                                  lastName: true,
                                  email: true,
                                },
                              },
                            },
                          },
                        },
                      },
                    },
                  },
                },
              },
              finalTotal: true,
            },
          },
        },
      },
    },
  });

export type CreditNoteForList = Prisma.CreditNoteGetPayload<
  typeof creditNoteForListValues
>;

export const creditNoteForPdfGenerationValues =
  Prisma.validator<Prisma.CreditNoteDefaultArgs>()({
    include: {
      order: {
        select: {
          onlineDetails: {
            select: {
              billingFirstName: true,
              billingLastName: true,
              billingAddress1: true,
              billingAddress2: true,
              billingZipCode: true,
              billingCountryCode: true,
              billingCity: true,
              billingBusinessName: true,
              invoice: {
                select: {
                  reference: true,
                  createdAt: true,
                },
              },
              user: {
                select: {
                  connectId: true,
                },
              },
            },
          },
        },
      },
      creditNoteItems: {
        include: {
          orderItem: {
            select: {
              id: true,
              basePrice: true,
              vatRate: true,
              finalTotal: true,
              totalDiscountAmount: true,
              buyableItem: {
                select: {
                  buyableType: true,
                },
              },
              ticket: {
                select: {
                  ticketType: { select: { name: true } },
                },
              },
              badge: {
                select: {
                  badgeType: { select: { name: true } },
                },
              },
            },
          },
        },
      },
      nf525SourceDocument: {
        select: {
          certificateMark: true,
          certificateCategory: true,
          certificateNumber: true,
          excerpt: true,
          softwareName: true,
          softwareVersion: true,
        },
      },
    },
  });

export type CreditNoteForPdfGeneration = Prisma.CreditNoteGetPayload<
  typeof creditNoteForPdfGenerationValues
>;

export const existingAcExpoCreditNoteValues =
  Prisma.validator<Prisma.CreditNoteDefaultArgs>()({
    select: {
      id: true,
      reference: true,
      createdAt: true,
      file: { select: { key: true, type: true } },
      creditNoteItems: { select: { orderItemId: true } },
    },
  });

export type ExistingAcExpoCreditNote = Prisma.CreditNoteGetPayload<
  typeof existingAcExpoCreditNoteValues
>;

export type AcExpoCreditNoteItem = {
  id: string;
  label: string;
  amount: number;
  disabled: boolean;
  buyableType: BuyableItemType;
};

export type AcExpoExistingCreditNote = {
  id: string;
  reference: string;
  fileKey: string | null;
  fileType: FileType | null;
  selectedOrderItemIds: string[];
};

export type AcExpoCreditNoteItemsResponse = {
  orderId: string;
  orderReference: string;
  professionalName: string;
  items: AcExpoCreditNoteItem[];
  latestCreditNote: AcExpoExistingCreditNote | null;
};

export type CreateAcExpoCreditNotePayload = {
  orderId: string;
  orderItemIds: string[];
};

export type CreateAcExpoCreditNoteResponse = {
  id: string;
  reference: string;
  fileKey: string | null;
  fileType: FileType | null;
};

export type BulkCreateCreditNotePayload = {
  eventId: string;
};

export type BulkCreateCreditNoteResult = {
  created: { orderId: string; creditNoteId: string }[];
  reAdded: { orderId: string; creditNoteId: string }[];
  skipped: { orderId: string; reason: string }[];
};

export const orderForBulkCreditNoteValues =
  Prisma.validator<Prisma.OrderDefaultArgs>()({
    select: {
      id: true,
      status: true,
      reference: true,
      onlineDetails: {
        select: {
          user: {
            select: {
              id: true,
              connectId: true,
              creditNoteCounter: true,
            },
          },
        },
      },
      creditNotes: {
        where: {
          status: {
            in: [
              CreditNoteStatus.awaiting,
              CreditNoteStatus.awaitingApproval,
              CreditNoteStatus.accepted,
              CreditNoteStatus.refused,
            ],
          },
        },
        select: {
          id: true,
          status: true,
          creditNoteItems: {
            select: {
              orderItemId: true,
            },
          },
        },
      },
      orderItems: {
        select: {
          basePrice: true,
          finalTotal: true,
          id: true,
          ticket: {
            select: {
              code: true,
              isRefunded: true,
              ScannedTicket: { select: { id: true } },
              ticketType: {
                select: {
                  eventId: true,
                  event: {
                    select: {
                      organiserId: true,
                    },
                  },
                  pricingId: true,
                },
              },
            },
          },
          badge: {
            select: {
              code: true,
              isRefunded: true,
              ScannedTicket: { select: { id: true } },
              badgeType: {
                select: {
                  eventId: true,
                  event: {
                    select: {
                      organiserId: true,
                    },
                  },
                  pricingId: true,
                },
              },
            },
          },
        },
      },
    },
  });

export type OrderForBulkCreditNote = Prisma.OrderGetPayload<
  typeof orderForBulkCreditNoteValues
>;
